// Globe.js - Three.js Globe Engine — v2 corrigé
import * as THREE from 'three';
import { getCategoryColor } from '../data/events';

const GLOBE_RADIUS = 5;
const POINT_BASE_SIZE = 0.06;
const ARC_SEGMENTS = 64;
const ATMOSPHERE_RADIUS = 5.2;

export function latLngToVector3(lat, lng, radius = GLOBE_RADIUS) {
  const phi   = (90 - lat) * (Math.PI / 180);
  const theta = (lng + 180) * (Math.PI / 180);
  return new THREE.Vector3(
    -radius * Math.sin(phi) * Math.cos(theta),
     radius * Math.cos(phi),
     radius * Math.sin(phi) * Math.sin(theta)
  );
}

function createArcPoints(start, end, segments = ARC_SEGMENTS) {
  const points = [];
  for (let i = 0; i <= segments; i++) {
    const t     = i / segments;
    const point = new THREE.Vector3().lerpVectors(start, end, t);
    const alt   = Math.sin(Math.PI * t) * GLOBE_RADIUS * 0.4;
    point.normalize().multiplyScalar(GLOBE_RADIUS + alt);
    points.push(point);
  }
  return points;
}

export class GlobeEngine {
  constructor(canvas) {
    this.canvas               = canvas;
    this.animationId          = null;
    this.points               = new Map();
    this.arcs                 = new Map();
    this.clock                = new THREE.Clock();
    this.isDragging           = false;
    this.previousMousePosition = { x: 0, y: 0 };
    // Rotation initiale : Europe/Atlantique face à l'utilisateur
    // lng=-10° → rotY = 10*PI/180 ≈ 0.175, lat=20° → rotX = -0.28
    this.targetRotation       = { x: -0.15, y: 0.175 };
    this.currentRotation      = { x: -0.15, y: 0.175 };
    this.targetZoom           = 14;
    this.currentZoom          = 14;
    this.onEventClick         = null;
    this.onHover              = null;
    this.raycaster            = new THREE.Raycaster();
    this.mouse                = new THREE.Vector2();
    this.hoveredPoint         = null;
    this.clickableObjects     = [];
    this.autoRotate           = true;
    this.autoRotateSpeed      = 0.0003;

    this._init();
    this._addEventListeners();
    this._animate();
  }

  _init() {
    const { width, height } = this.canvas.getBoundingClientRect();

    this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, antialias: true, alpha: true });
    this.renderer.setClearColor(0x000000, 0); // fond transparent
    this.renderer.setSize(width, height);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.2;

    this.scene  = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
    this.camera.position.z = this.targetZoom;
    // Pas de setViewOffset — on translate le globeGroup à la place
    // pour que le raycaster reste correct
    this.globeGroup  = new THREE.Group();
    // Décale le globe à gauche (≈ 22% de la largeur)
    const fov0 = this.camera.fov * Math.PI / 180;
    const dist0 = this.camera.position.z;
    const visH0 = 2 * Math.tan(fov0/2) * dist0;
    const visW0 = visH0 * (width/height);
    this.globeGroup.position.x = -(visW0 * 0.22);
    this.scene.add(this.globeGroup);
    this.pointsGroup = new THREE.Group();
    this.globeGroup.add(this.pointsGroup);
    this.arcsGroup   = new THREE.Group();
    this.globeGroup.add(this.arcsGroup);

    this._createGlobe();
    this._createAtmosphere();
    this._createStarfield();
    this._createLights();
  }

  _createGlobe() {
    const geometry = new THREE.SphereGeometry(GLOBE_RADIUS, 64, 64);
    const loader   = new THREE.TextureLoader();
    const material = new THREE.MeshPhongMaterial({
      map:         loader.load('/earth.jpg'),
      bumpMap:     loader.load('/earth_bump.jpg'),
      bumpScale:   0.05,
      specularMap: loader.load('/earth_specular.jpg'),
      specular:    new THREE.Color(0x333333),
      shininess:   25,
    });
    this.globeMesh = new THREE.Mesh(geometry, material);
    this.globeGroup.add(this.globeMesh);
  }

  _createAtmosphere() {
    const geometry = new THREE.SphereGeometry(ATMOSPHERE_RADIUS, 64, 64);
    const material = new THREE.ShaderMaterial({
      uniforms: {
        glowColor:  { value: new THREE.Color(0x1a6fa8) },
        viewVector: { value: this.camera.position },
      },
      vertexShader: `
        uniform vec3 viewVector;
        varying float intensity;
        void main() {
          vec3 vNormal = normalize(normalMatrix * normal);
          vec3 vNormel = normalize(normalMatrix * viewVector);
          intensity = pow(0.7 - dot(vNormal, vNormel), 2.0);
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 glowColor;
        varying float intensity;
        void main() {
          vec3 glow = glowColor * intensity;
          gl_FragColor = vec4(glow, intensity * 0.6);
        }
      `,
      side: THREE.FrontSide,
      blending: THREE.AdditiveBlending,
      transparent: true,
      depthWrite: false,
    });
    this.atmosphere = new THREE.Mesh(geometry, material);
    this.scene.add(this.atmosphere);
  }

  _createStarfield() {
    const geo       = new THREE.BufferGeometry();
    const count     = 8000;
    const positions = new Float32Array(count * 3);
    const sizes     = new Float32Array(count);
    for (let i = 0; i < count; i++) {
      const theta = Math.random() * Math.PI * 2;
      const phi   = Math.acos(Math.random() * 2 - 1);
      const r     = 300 + Math.random() * 200;
      positions[i * 3]     = r * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = r * Math.cos(phi);
      positions[i * 3 + 2] = r * Math.sin(phi) * Math.sin(theta);
      sizes[i] = Math.random() * 1.5 + 0.3;
    }
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geo.setAttribute('size',     new THREE.BufferAttribute(sizes, 1));
    const mat = new THREE.ShaderMaterial({
      uniforms: { color: { value: new THREE.Color(0xffffff) } },
      vertexShader:   `attribute float size; void main() { gl_PointSize = size; gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }`,
      fragmentShader: `uniform vec3 color; void main() { float d = length(gl_PointCoord - vec2(0.5)); if(d>0.5) discard; gl_FragColor = vec4(color, (1.0 - smoothstep(0.3,0.5,d)) * 0.8); }`,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    this.starfield = new THREE.Points(geo, mat);
    this.scene.add(this.starfield);
  }

  _createLights() {
    this.scene.add(new THREE.AmbientLight(0x222233, 0.8));
    const sun = new THREE.DirectionalLight(0xfff8e7, 2.5);
    sun.position.set(20, 10, 15);
    this.scene.add(sun);
    const fill = new THREE.DirectionalLight(0x1a3a6b, 0.4);
    fill.position.set(-20, -10, -15);
    this.scene.add(fill);
  }

  // ── Public API ────────────────────────────────────────────────────────────

  setEvents(eventList) {
    this._clearPoints();
    this._clearArcs();
    eventList.forEach(ev => this._addPoint(ev));
    this.clickableObjects = [...this.points.values()].map(p => p.mesh);
  }

  setConnections(eventList, allEvents) {
    this._clearArcs();
    const rendered = new Set();
    eventList.forEach(event => {
      if (!event.connections) return;
      event.connections.forEach(connId => {
        const pairKey  = [event.id, connId].sort().join('-');
        if (rendered.has(pairKey)) return;
        rendered.add(pairKey);
        const connEvent = allEvents.find(e => e.id === connId);
        if (!connEvent) return;
        if (!eventList.find(e => e.id === connId)) return;
        this._addArc(event, connEvent);
      });
    });
  }

  _addPoint(event) {
    const position = latLngToVector3(event.lat, event.lng);
    const color    = getCategoryColor(event.category);
    const size     = POINT_BASE_SIZE * (0.8 + event.importance * 0.4);

    const geometry = new THREE.ConeGeometry(size, size * 3, 8);
    geometry.rotateX(-Math.PI / 2);
    const material = new THREE.MeshBasicMaterial({ color: new THREE.Color(color), transparent: true, opacity: 0 });
    const mesh     = new THREE.Mesh(geometry, material);
    mesh.position.copy(position);
    mesh.lookAt(new THREE.Vector3(0, 0, 0));
    mesh.rotateX(Math.PI);
    mesh.userData = { event, birthTime: this.clock.getElapsedTime() };

    const ringGeo  = new THREE.RingGeometry(size * 1.2, size * 2.5, 16);
    const ringMat  = new THREE.MeshBasicMaterial({ color: new THREE.Color(color), transparent: true, opacity: 0, side: THREE.DoubleSide });
    const ring     = new THREE.Mesh(ringGeo, ringMat);
    ring.position.copy(position);
    ring.lookAt(new THREE.Vector3(0, 0, 0));

    this.pointsGroup.add(mesh);
    this.pointsGroup.add(ring);
    this.points.set(event.id, { mesh, ring, material, ringMat, event });
  }

  _addArc(eventA, eventB) {
    const startVec  = latLngToVector3(eventA.lat, eventA.lng);
    const endVec    = latLngToVector3(eventB.lat, eventB.lng);
    const arcPoints = createArcPoints(startVec, endVec);
    const geometry  = new THREE.BufferGeometry().setFromPoints(arcPoints);
    const material  = new THREE.LineBasicMaterial({ color: 0x4488ff, transparent: true, opacity: 0 });
    const line      = new THREE.Line(geometry, material);
    line.userData   = { birthTime: this.clock.getElapsedTime() };
    this.arcsGroup.add(line);
    const key = [eventA.id, eventB.id].sort().join('-');
    this.arcs.set(key, { line, material });
  }

  _clearPoints() {
    this.points.forEach(({ mesh, ring }) => {
      this.pointsGroup.remove(mesh);
      this.pointsGroup.remove(ring);
      mesh.geometry.dispose(); mesh.material.dispose();
      ring.geometry.dispose(); ring.material.dispose();
    });
    this.points.clear();
    this.clickableObjects = [];
  }

  _clearArcs() {
    this.arcs.forEach(({ line }) => {
      this.arcsGroup.remove(line);
      line.geometry.dispose(); line.material.dispose();
    });
    this.arcs.clear();
  }

  // ── ZOOM CORRIGÉ ──────────────────────────────────────────────────────────
  // Bug : targetLng / targetLat mal calculés → globe pivotait vers le bas
  // Fix : rotation Y = longitude en radians (inversée), rotation X = latitude (limitée)
  zoomToEvent(event, callback) {
    this.autoRotate  = false;
    this.targetZoom  = 8;

    // Rotation Y : faire pivoter le globe pour centrer la longitude
    // Le globe tourne dans le sens opposé à la longitude
    const targetRotY = -(event.lng * Math.PI / 180);

    // Rotation X : incliner pour la latitude (clampé à ±80° pour ne pas retourner le globe)
    const targetRotX = -(event.lat * Math.PI / 180) * 0.8;
    const clampedX   = Math.max(-Math.PI / 2.5, Math.min(Math.PI / 2.5, targetRotX));

    this._animateTo(clampedX, targetRotY, 8, 1400, callback);
  }

  _animateTo(targetRotX, targetRotY, targetZ, duration, callback) {
    const startX = this.currentRotation.x;
    const startY = this.currentRotation.y;
    const startZ = this.currentZoom;
    const start  = performance.now();

    // Normalise Y pour prendre le chemin le plus court
    let dY = ((targetRotY - startY) % (Math.PI * 2));
    if (dY > Math.PI)  dY -= Math.PI * 2;
    if (dY < -Math.PI) dY += Math.PI * 2;
    const endY = startY + dY;

    const tick = (now) => {
      const t    = Math.min((now - start) / duration, 1);
      const ease = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

      this.targetRotation.x = startX + (targetRotX - startX) * ease;
      this.targetRotation.y = startY + (endY - startY) * ease;
      this.targetZoom       = startZ + (targetZ - startZ) * ease;

      if (t < 1) requestAnimationFrame(tick);
      else if (callback) callback();
    };
    requestAnimationFrame(tick);
  }

  resetView() {
    this.targetZoom = 14;
    this.autoRotate = true;
  }

  highlightEvent(eventId) {
    this.points.forEach(({ mesh, ring, material, ringMat }, id) => {
      if (id === eventId) {
        material.opacity = 1;
        ringMat.opacity  = 0.6;
        mesh.scale.setScalar(1.5);
      } else {
        mesh.scale.setScalar(1);
      }
    });
  }

  clearHighlight() {
    this.points.forEach(({ mesh }) => mesh.scale.setScalar(1));
  }

  // ── Event Listeners ───────────────────────────────────────────────────────

  _addEventListeners() {
    this._onMouseDown = this._handleMouseDown.bind(this);
    this._onMouseMove = this._handleMouseMove.bind(this);
    this._onMouseUp   = this._handleMouseUp.bind(this);
    this._onWheel     = this._handleWheel.bind(this);
    this._onClick     = this._handleClick.bind(this);
    this._onResize    = this._handleResize.bind(this);
    this._onTouchStart = this._handleTouchStart.bind(this);
    this._onTouchMove  = this._handleTouchMove.bind(this);
    this._onTouchEnd   = this._handleTouchEnd.bind(this);

    this.canvas.addEventListener('mousedown',  this._onMouseDown);
    this.canvas.addEventListener('mousemove',  this._onMouseMove);
    this.canvas.addEventListener('mouseup',    this._onMouseUp);
    this.canvas.addEventListener('mouseleave', this._onMouseUp);
    this.canvas.addEventListener('wheel',      this._onWheel, { passive: true });
    this.canvas.addEventListener('click',      this._onClick);
    this.canvas.addEventListener('touchstart', this._onTouchStart, { passive: true });
    this.canvas.addEventListener('touchmove',  this._onTouchMove,  { passive: true });
    this.canvas.addEventListener('touchend',   this._onTouchEnd);
    window.addEventListener('resize',          this._onResize);
  }

  _handleMouseDown(e) {
    this.isDragging = true;
    this.autoRotate = false;
    this.previousMousePosition = { x: e.clientX, y: e.clientY };
  }

  _handleMouseMove(e) {
    if (!this.isDragging) { this._updateHover(e.clientX, e.clientY); return; }
    const dx = e.clientX - this.previousMousePosition.x;
    const dy = e.clientY - this.previousMousePosition.y;
    this.targetRotation.y += dx * 0.005;
    this.targetRotation.x += dy * 0.005;
    this.targetRotation.x = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, this.targetRotation.x));
    this.previousMousePosition = { x: e.clientX, y: e.clientY };
  }

  _handleMouseUp()  { this.isDragging = false; }

  _handleWheel(e) {
    this.targetZoom += e.deltaY * 0.01;
    this.targetZoom  = Math.max(6, Math.min(20, this.targetZoom));
  }

  _handleClick(e) {
    if (!this.onEventClick) return;
    const rect = this.canvas.getBoundingClientRect();
    this.mouse.x = ((e.clientX - rect.left) / rect.width)  * 2 - 1;
    this.mouse.y = -((e.clientY - rect.top)  / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.mouse, this.camera);
    const hits = this.raycaster.intersectObjects(this.clickableObjects);
    if (hits.length > 0) {
      const ev = hits[0].object.userData.event;
      if (ev) this.onEventClick(ev);
    }
  }

  _updateHover(clientX, clientY) {
    const rect = this.canvas.getBoundingClientRect();
    this.mouse.x = ((clientX - rect.left) / rect.width)  * 2 - 1;
    this.mouse.y = -((clientY - rect.top)  / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.mouse, this.camera);
    const hits = this.raycaster.intersectObjects(this.clickableObjects);
    const hit  = hits.length > 0 ? hits[0].object.userData.event : null;
    if (hit !== this.hoveredPoint) {
      this.hoveredPoint = hit;
      this.canvas.style.cursor = hit ? 'pointer' : 'grab';
      if (this.onHover) this.onHover(hit);
    }
  }

  _handleTouchStart(e) {
    if (e.touches.length === 1) {
      this.isDragging = true;
      this.autoRotate = false;
      this.previousMousePosition = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
    this._touchStartDistance = e.touches.length === 2
      ? Math.hypot(e.touches[1].clientX - e.touches[0].clientX, e.touches[1].clientY - e.touches[0].clientY)
      : null;
  }

  _handleTouchMove(e) {
    if (e.touches.length === 1 && this.isDragging) {
      const dx = e.touches[0].clientX - this.previousMousePosition.x;
      const dy = e.touches[0].clientY - this.previousMousePosition.y;
      this.targetRotation.y += dx * 0.005;
      this.targetRotation.x += dy * 0.005;
      this.targetRotation.x = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, this.targetRotation.x));
      this.previousMousePosition = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    } else if (e.touches.length === 2 && this._touchStartDistance) {
      const dist  = Math.hypot(e.touches[1].clientX - e.touches[0].clientX, e.touches[1].clientY - e.touches[0].clientY);
      this.targetZoom += (this._touchStartDistance - dist) * 0.05;
      this.targetZoom  = Math.max(6, Math.min(20, this.targetZoom));
      this._touchStartDistance = dist;
    }
  }

  _handleTouchEnd() { this.isDragging = false; }

  _handleResize() {
    const { width, height } = this.canvas.getBoundingClientRect();
    this.camera.aspect = width / height;
    this.camera.clearViewOffset();
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);
    // Recalcule la position du globe
    const fov  = this.camera.fov * Math.PI / 180;
    const dist = this.camera.position.z;
    const visH = 2 * Math.tan(fov/2) * dist;
    const visW = visH * (width/height);
    if (this.globeGroup) this.globeGroup.position.x = -(visW * 0.22);
  }

  // ── Animation ─────────────────────────────────────────────────────────────

  _animate() {
    this.animationId = requestAnimationFrame(() => this._animate());
    const elapsed = this.clock.getElapsedTime();
    this.clock.getDelta(); // keep clock ticking

    if (this.autoRotate) this.targetRotation.y += this.autoRotateSpeed;

    this.currentRotation.x += (this.targetRotation.x - this.currentRotation.x) * 0.08;
    this.currentRotation.y += (this.targetRotation.y - this.currentRotation.y) * 0.08;
    this.currentZoom       += (this.targetZoom - this.currentZoom) * 0.08;

    this.globeGroup.rotation.x = this.currentRotation.x;
    this.globeGroup.rotation.y = this.currentRotation.y;
    this.camera.position.z     = this.currentZoom;

    this.points.forEach(({ mesh, ring, material, ringMat }) => {
      const age    = elapsed - mesh.userData.birthTime;
      const fadeIn = Math.min(age / 0.8, 1);
      const pulse  = 1 + Math.sin(elapsed * 2 + mesh.userData.event.id.charCodeAt(1)) * 0.15;
      mesh.scale.setScalar(fadeIn * pulse);
      material.opacity = fadeIn * 0.9;
      const ripple = (elapsed * 0.5) % 1;
      ring.scale.setScalar(fadeIn * (1 + ripple * 2));
      ringMat.opacity = fadeIn * (1 - ripple) * 0.4;
    });

    this.arcs.forEach(({ line, material }) => {
      const age      = elapsed - line.userData.birthTime;
      const progress = Math.min(age / 1.5, 1);
      material.opacity = progress * 0.35;
      line.geometry.setDrawRange(0, Math.floor(progress * line.geometry.attributes.position.count));
    });

    if (this.atmosphere?.material?.uniforms) {
      this.atmosphere.material.uniforms.viewVector.value.copy(this.camera.position);
    }

    this.renderer.render(this.scene, this.camera);
  }

  destroy() {
    cancelAnimationFrame(this.animationId);
    this.canvas.removeEventListener('mousedown',  this._onMouseDown);
    this.canvas.removeEventListener('mousemove',  this._onMouseMove);
    this.canvas.removeEventListener('mouseup',    this._onMouseUp);
    this.canvas.removeEventListener('mouseleave', this._onMouseUp);
    this.canvas.removeEventListener('wheel',      this._onWheel);
    this.canvas.removeEventListener('click',      this._onClick);
    this.canvas.removeEventListener('touchstart', this._onTouchStart);
    this.canvas.removeEventListener('touchmove',  this._onTouchMove);
    this.canvas.removeEventListener('touchend',   this._onTouchEnd);
    window.removeEventListener('resize',          this._onResize);
    this._clearPoints();
    this._clearArcs();
    this.renderer.dispose();
  }
}
